'use client'

import { useAuth } from '@/components/auth-provider'
import { ThemeToggle } from '@/components/theme-toggle'
import { Dock, DockIcon } from '@/components/magicui/dock'
import { Home, BookOpen, Calendar, UserCircle, Menu, PenSquare, Bell, Settings, Info, LogIn, UserPlus, Divide } from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useState, useEffect } from 'react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

export default function DockNav() {
  const { user } = useAuth()
  const pathname = usePathname() || '/'
  const [showDock, setShowDock] = useState(false)
  const [notificationCount, setNotificationCount] = useState(0)

  // Show dock on mobile for all users
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768
      setShowDock(isMobile)
    }

    // Initialize
    handleResize()

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Mock notification count - replace with actual implementation
  useEffect(() => {
    if (user) {
      setNotificationCount(3)
    }
  }, [user])

  if (!showDock) return null

  const dockStyles = cn(
    "border-border bg-white/95 dark:bg-slate-900/90 backdrop-blur-xl shadow-lg",
    "border py-2 px-2.5 rounded-2xl"
  )

  const getIconStyles = (isActive: boolean) => cn(
    "bg-transparent transition-colors flex items-center justify-center h-full w-full",
    isActive ? "text-primary" : "text-muted-foreground"
  )

  // Different dock icons based on authentication status
  if (user) {
    // Authenticated user dock
    return (
      <div className="fixed bottom-4 left-0 right-0 z-50 flex justify-center md:hidden">
        <Dock className={dockStyles}>
          <DockIcon className="bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors rounded-full">
            <Link href="/" className="flex items-center justify-center h-full w-full">
              <Home className={getIconStyles(pathname === '/')} />
            </Link>
          </DockIcon>
          
          <DockIcon className="bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors rounded-full">
            <Link href="/blog" className="flex items-center justify-center h-full w-full">
              <BookOpen className={getIconStyles(pathname.startsWith('/blog'))} />
            </Link>
          </DockIcon>
          
          <DockIcon className="bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors rounded-full">
            <Link href="/events" className="flex items-center justify-center h-full w-full">
              <Calendar className={getIconStyles(pathname.startsWith('/events'))} />
            </Link>
          </DockIcon>
          
          <DockIcon className="bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors rounded-full">
            <Link href="/dashboard/new-article" className="flex items-center justify-center h-full w-full">
              <PenSquare className={getIconStyles(pathname.startsWith('/dashboard/new-article'))} />
            </Link>
          </DockIcon>
          
          <DockIcon className="bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors rounded-full">
            <Link href="/dashboard/notifications" className="flex items-center justify-center h-full w-full relative">
              <Bell className={getIconStyles(pathname.startsWith('/dashboard/notifications'))} />
              {notificationCount > 0 && (
                <Badge className="absolute -top-0.5 -right-0.5 h-4 min-w-4 flex items-center justify-center rounded-full text-[10px] px-1 py-0 bg-destructive text-destructive-foreground">
                  {notificationCount}
                </Badge>
              )}
            </Link>
          </DockIcon>
          
          <DockIcon className="bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors rounded-full">
            <Link href="/dashboard" className="flex items-center justify-center h-full w-full">
              <UserCircle className={getIconStyles(pathname.startsWith('/dashboard') && !pathname.startsWith('/dashboard/new-article') && !pathname.startsWith('/dashboard/notifications'))} />
            </Link>
          </DockIcon>
          
          <DockIcon className="bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors rounded-full">
            <div className="flex items-center justify-center h-full w-full">
              <ThemeToggle variant="icon-only" />
            </div>
          </DockIcon>
        </Dock>
      </div>
    )
  }
  
  // Guest user dock
  return (
    <div className="fixed bottom-4 left-0 right-0 z-50 flex justify-center md:hidden">
      <Dock className={dockStyles}>
        <DockIcon className="bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors rounded-full">
          <Link href="/" className="flex items-center justify-center h-full w-full">
            <Home className={getIconStyles(pathname === '/')} />
          </Link>
        </DockIcon>
        
        <DockIcon className="bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors rounded-full">
          <Link href="/blog" className="flex items-center justify-center h-full w-full">
            <BookOpen className={getIconStyles(pathname.startsWith('/blog'))} />
          </Link>
        </DockIcon>
        
        <DockIcon className="bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors rounded-full">
          <Link href="/events" className="flex items-center justify-center h-full w-full">
            <Calendar className={getIconStyles(pathname.startsWith('/events'))} />
          </Link>
        </DockIcon>
        
        {/* Visual divider */}
        <div className="w-px h-8 bg-slate-200 dark:bg-slate-700 mx-1 self-center" />
        
        <DockIcon className="bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors rounded-full">
          <Link href="/auth/login" className="flex items-center justify-center h-full w-full">
            <LogIn className={getIconStyles(pathname.startsWith('/auth/login'))} />
          </Link>
        </DockIcon>
        
        {/* Custom sign up button that doesn't get cut off */}
        <div className="h-[42px] flex items-center justify-center rounded-full overflow-visible mx-1">
          <Link 
            href="/auth/register" 
            className="bg-primary hover:bg-primary/90 transition-colors rounded-full h-[42px] px-4 flex items-center justify-center"
          >
            <span className="text-primary-foreground text-xs font-medium whitespace-nowrap">Sign Up</span>
          </Link>
        </div>
        
        <DockIcon className="bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors rounded-full">
          <div className="flex items-center justify-center h-full w-full">
            <ThemeToggle variant="icon-only" />
          </div>
        </DockIcon>
      </Dock>
    </div>
  )
} 